// Raw-body uploads over XMLHttpRequest (fetch reports no upload progress):
// chat attachments and voice clips, profile photos.
import { ApiError } from "./api";
import type { SessionManager } from "./auth/session";

export interface BlobRequest {
  method: "POST" | "PUT";
  path: string;
  query: Record<string, string | number | undefined>;
  body: Blob;
  contentType: string;
  onProgress?: (fraction: number) => void;
  signal: AbortSignal;
}

function xhr(token: string, request: BlobRequest): Promise<{ status: number; body: unknown }> {
  return new Promise((resolve, reject) => {
    const url = new URL(`/api/v1${request.path}`, window.location.origin);
    for (const [key, value] of Object.entries(request.query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
    const x = new XMLHttpRequest();
    x.open(request.method, url);
    x.setRequestHeader("Authorization", `Bearer ${token}`);
    x.setRequestHeader("Content-Type", request.contentType);
    x.responseType = "json";
    x.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) request.onProgress?.(event.loaded / event.total);
    };
    const abort = () => x.abort();
    const settle = () => request.signal.removeEventListener("abort", abort);
    x.onload = () => (settle(), resolve({ status: x.status, body: x.response as unknown }));
    x.onerror = () => (settle(), reject(new TypeError("Network error")));
    x.onabort = () => (settle(), reject(new DOMException("Upload cancelled", "AbortError")));
    if (request.signal.aborted) {
      reject(new DOMException("Upload cancelled", "AbortError"));
      return;
    }
    request.signal.addEventListener("abort", abort, { once: true });
    x.send(request.body);
  });
}

/** Sends a raw body with the session's token, refreshing once on a 401 (as `authFetch` does). */
export async function sendBlob<T>(session: SessionManager, request: BlobRequest): Promise<T> {
  const current = await session.activeSession();
  if (!current) throw new ApiError(401, undefined);
  let response = await xhr(current.accessToken, request);
  if (response.status === 401) {
    const refreshed = await session.refresh(current);
    if (refreshed) response = await xhr(refreshed.accessToken, request);
  }
  if (response.status >= 200 && response.status < 300) return response.body as T;
  const problem = typeof response.body === "object" && response.body !== null ? response.body : undefined;
  throw new ApiError(response.status, problem);
}
